import React, { useState, useRef, useEffect } from 'react';
import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { FiltersState } from '../../hooks/useFilters';
import CustomCheckbox from '../ui/CustomCheckbox';
import DropdownItem from '../ui/DropdownItem';
import { fetchHHAreas, fetchHHIndustries, type AreaOption, type IndustryOption } from '../../services/hhDictionariesService';

interface FilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  filterType: string;
  filters: FiltersState;
  onFiltersChange: (filters: Partial<FiltersState>) => void;
  scrollToSection?: string;
}

// Custom Radio Component
const CustomRadio: React.FC<{
  checked: boolean;
  onChange: () => void;
  label: string;
}> = ({ checked, onChange, label }) => {
  return (
    <div className="flex items-center space-x-2">
      <input
        type="radio"
        checked={checked}
        onChange={onChange}
        className="w-4 h-4 text-[#e16349] border-gray-300 focus:ring-[#e16349]"
      />
      <label className="text-sm text-gray-700">{label}</label>
    </div>
  );
};

export const FilterModal: React.FC<FilterModalProps> = ({
  isOpen,
  onClose,
  filterType,
  filters,
  onFiltersChange,
  scrollToSection
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [areas, setAreas] = useState<AreaOption[]>([]);
  const [areasLoading, setAreasLoading] = useState(false);
  const [areasError, setAreasError] = useState<string | null>(null);
  const [industries, setIndustries] = useState<IndustryOption[]>([]);
  const [industriesLoading, setIndustriesLoading] = useState(false);
  const [industriesError, setIndustriesError] = useState<string | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Инициализируем выбранные элементы из фильтров
      if (filterType === 'industry') {
        // Используем selectedIndustries если есть, иначе industry
        const selectedIndustryIds = filters.selectedIndustries || (filters.industry ? [filters.industry] : []);
        setSelectedItems(new Set(selectedIndustryIds));
      } else if (filterType === 'specialization' && filters.specialization) {
        setSelectedItems(new Set([filters.specialization]));
      } else if (filterType === 'geography') {
        // Используем cityIds если есть, иначе cities
        const selectedCityIds = filters.cityIds || filters.cities || [];
        setSelectedItems(new Set(selectedCityIds));
      }
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, filterType, filters]);

  useEffect(() => {
    const loadAreas = async () => {
      if (!isOpen || filterType !== 'geography') {
        return;
      }
      setAreasLoading(true);
      setAreasError(null);
      try {
        const data = await fetchHHAreas();
        setAreas(data);
      } catch (error) {
        setAreasError(error instanceof Error ? error.message : 'Не удалось получить список городов');
        setAreas([]);
      } finally {
        setAreasLoading(false);
      }
    };

    void loadAreas();
  }, [isOpen, filterType]);

  useEffect(() => {
    const loadIndustries = async () => {
      if (!isOpen || filterType !== 'industry') {
        return;
      }
      setIndustriesLoading(true);
      setIndustriesError(null);
      try {
        const data = await fetchHHIndustries();
        setIndustries(data);
      } catch (error) {
        setIndustriesError(error instanceof Error ? error.message : 'Не удалось получить список отраслей');
        setIndustries([]);
      } finally {
        setIndustriesLoading(false);
      }
    };

    void loadIndustries();
  }, [isOpen, filterType]);

  // Автоматическая прокрутка к нужной секции
  useEffect(() => {
    if (isOpen && scrollToSection && modalRef.current) {
      const timer = setTimeout(() => {
        const element = modalRef.current?.querySelector(`[data-section="${scrollToSection}"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen, scrollToSection]);

  if (!isOpen) return null;

  const getData = () => {
    switch (filterType) {
      case 'industry':
        return industries;
      case 'specialization':
        return []; // Пока нет API для специализаций
      case 'geography':
        return areas;
      default:
        return [];
    }
  };

  const handleToggleItem = (id: string, checked: boolean) => {
    const newSelected = new Set(selectedItems);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedItems(newSelected);
  };

  const handleReset = () => {
    setSelectedItems(new Set());
  };

  const handleSave = () => {
    if (filterType === 'industry') {
      const selectedIndustryIds = Array.from(selectedItems);
      const selectedIndustries = industries.filter(industry => selectedIndustryIds.includes(industry.id));
      onFiltersChange({ 
        industry: selectedIndustries.map(industry => industry.name).join(', '),
        selectedIndustries: selectedIndustryIds
      });
    } else if (filterType === 'specialization') {
      const selectedArray = Array.from(selectedItems);
      onFiltersChange({ specialization: selectedArray[0] || '' });
    } else if (filterType === 'geography') {
      const selectedCityIds = Array.from(selectedItems);
      const selectedCities = areas.filter(area => selectedCityIds.includes(area.id));
      onFiltersChange({ 
        cities: selectedCities.map(city => city.name),
        cityIds: selectedCityIds
      });
    }
    onClose();
  };

  const renderContent = () => {
    switch (filterType) {
      case 'geography':
        if (areasLoading) {
          return (
            <div className="flex items-center justify-center h-32 text-sm text-gray-500">
              Загрузка городов...
            </div>
          );
        }

        if (areasError) {
          return (
            <div className="flex flex-col items-start gap-3 text-sm text-red-600">
              <span>Не удалось загрузить города: {areasError}</span>
              <button
                className="text-[#e16349] hover:underline"
                onClick={() => {
                  setAreasError(null);
                  setAreas([]);
                  setAreasLoading(true);
                  void fetchHHAreas()
                    .then((data: AreaOption[]) => {
                      setAreas(data);
                    })
                    .catch((err: unknown) => {
                      setAreasError(err instanceof Error ? err.message : 'Не удалось получить список городов');
                    })
                    .finally(() => {
                      setAreasLoading(false);
                    });
                }}
              >
                Повторить попытку
              </button>
            </div>
          );
        }

        const filteredCities = areas.filter(city => 
          !searchTerm || city.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
        if (filteredCities.length === 0) {
          return (
            <div className="flex items-center justify-center h-32 text-sm text-gray-500">
              Ничего не найдено
            </div>
          );
        }
        return (
          <div className="space-y-6">
            {/* Города */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Города</h3>
              <div className="max-h-48 overflow-y-auto space-y-2">
                {filteredCities.map((city) => (
                  <CustomCheckbox
                    key={city.id}
                    checked={selectedItems.has(city.id)}
                    onChange={(checked) => handleToggleItem(city.id, checked)}
                    label={city.name}
                  />
                ))}
              </div>
            </div>
          </div>
        );

      case 'industry':
        if (industriesLoading) {
          return (
            <div className="flex items-center justify-center h-32 text-sm text-gray-500">
              Загрузка отраслей...
            </div>
          );
        }

        if (industriesError) {
          return (
            <div className="flex flex-col items-start gap-3 text-sm text-red-600">
              <span>Не удалось загрузить отрасли: {industriesError}</span>
              <button
                className="text-[#e16349] hover:underline"
                onClick={() => {
                  setIndustriesError(null);
                  setIndustries([]);
                  setIndustriesLoading(true);
                  void fetchHHIndustries()
                    .then((data: IndustryOption[]) => {
                      setIndustries(data);
                    })
                    .catch((err: unknown) => {
                      setIndustriesError(err instanceof Error ? err.message : 'Не удалось получить список отраслей');
                    })
                    .finally(() => {
                      setIndustriesLoading(false);
                    });
                }}
              >
                Повторить попытку
              </button>
            </div>
          );
        }

        const filteredIndustries = industries.filter(industry => 
          !searchTerm || industry.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
        if (filteredIndustries.length === 0) {
          return (
            <div className="flex items-center justify-center h-32 text-sm text-gray-500">
              Ничего не найдено
            </div>
          );
        }
        return (
          <div className="space-y-6">
            {/* Отрасли */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Отрасли</h3>
              <div className="max-h-96 overflow-y-auto">
                {filteredIndustries.map((industry) => (
                  <DropdownItem
                    key={industry.id}
                    id={industry.id}
                    name={industry.name}
                    children={industry.industries}
                    checked={selectedItems.has(industry.id)}
                    onChange={handleToggleItem}
                    level={0}
                  />
                ))}
              </div>
            </div>
          </div>
        );

      case 'specialization':
        return (
          <div className="flex items-center justify-center h-32 text-sm text-gray-500">
            Фильтр специализаций пока не реализован
          </div>
        );

      default:
        return (
          <div className="flex items-center justify-center h-32 text-sm text-gray-500">
            Неизвестный тип фильтра
          </div>
        );
    }
  };

  const getTitle = () => {
    switch (filterType) {
      case 'geography':
        return 'Фильтр по географии';
      case 'industry':
        return 'Фильтр по отраслям';
      case 'specialization':
        return 'Фильтр по специализациям';
      default:
        return 'Фильтр';
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div
          ref={modalRef}
          className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">{getTitle()}</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Search */}
          <div className="p-6 border-b border-gray-200">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Поиск"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#e16349]"
              />
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {renderContent()}
          </div>

          {/* Footer */}
          <div className="p-4">
            <div className="flex justify-between">
              <button
                onClick={handleReset}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Сбросить
              </button>
              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Отменить
                </button>
                <button
                  onClick={handleSave}
                  className="px-4 py-2 bg-[#e16349] text-white rounded-lg hover:bg-[#d14d3a] transition-colors"
                >
                  Сохранить
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FilterModal;